import { Link } from 'react-router-dom';
import { MapPin, Mail, Music2, Facebook, Instagram, Youtube } from 'lucide-react';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';

const footerLinks = [
  { to: '/about', label: 'About' },
  { to: '/lessons', label: 'Lessons' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/testimonials', label: 'Testimonials' },
  { to: '/faq', label: 'FAQ' },
  { to: '/contact', label: 'Contact' },
];

export default function Footer() {
  const settings = useSiteSettings();
  const social = settings.socialLinks || {};
  const hasSocial = social.facebook || social.instagram || social.youtube;

  return (
    <footer data-testid="site-footer" className="bg-[#4B1D5E] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to="/" className="inline-block mb-4">
              <div className="flex items-center gap-2">
                <Music2 className="h-8 w-8 text-[#B088BC]" />
                <span className="text-2xl font-bold">Singvolve</span>
              </div>
            </Link>
            <p className="text-white/70 text-sm leading-relaxed max-w-xs">
              Evolve your voice with professional, supportive singing lessons in Bristol. For children and adults of all abilities.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-[#B088BC]">Quick Links</h4>
            <nav className="flex flex-col gap-2">
              {footerLinks.map((link) => (
                <Link key={link.to} to={link.to} className="text-white/70 hover:text-white text-sm transition-colors">
                  {link.label}
                </Link>
              ))}
              <Link to="/portal-login" className="text-white/70 hover:text-white text-sm transition-colors">
                Student Login
              </Link>
            </nav>
          </div>

          {/* Location of Lessons */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-[#B088BC]">Location of Lessons</h4>
            <div className="space-y-3">
              <div className="flex items-start gap-3 text-sm text-white/70">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[#B088BC]" />
                <span>Bristol Cathedral Choir School,<br />College Square, Bristol, BS1 5TS</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-white/70">
                <Mail className="h-4 w-4 shrink-0 text-[#B088BC]" />
                <a href={`mailto:${settings.adminEmail}`} className="hover:text-white transition-colors">
                  {settings.adminEmail}
                </a>
              </div>
            </div>
          </div>

          {/* Follow Us */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-[#B088BC]">Follow</h4>
            <div className="flex items-center gap-4" data-testid="footer-social-links">
              {social.facebook && (
                <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="social-facebook">
                  <Facebook className="h-5 w-5 text-white" />
                </a>
              )}
              {social.instagram && (
                <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="social-instagram">
                  <Instagram className="h-5 w-5 text-white" />
                </a>
              )}
              {social.youtube && (
                <a href={social.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="social-youtube">
                  <Youtube className="h-5 w-5 text-white" />
                </a>
              )}
              {!hasSocial && (
                <p className="text-white/50 text-sm">Social links coming soon</p>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-xs">
            &copy; {new Date().getFullYear()} Singvolve. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-white/50 text-xs">
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <span>|</span>
            <Link to="/portal-login" className="hover:text-white transition-colors">Student Login</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
